import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Loader2Icon, MicIcon, SendIcon, SquareIcon } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../../components/base";
import { AutosizeTextarea } from "../../../components/base-plus";
import { AttachmentCardList } from "../../../components/Attachment";
import { useUiTranslation } from "../../../i18n";
import { VoiceRecorderErrorCode, useVoiceRecorder, } from "../../../hooks/useVoiceRecorder";
import { FileUploadButton } from "./FileUploadButton";
const formatElapsedTime = (elapsedMs) => {
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};
export const QuestionPanel = ({ className, message, isAsking, isSubmitting = false, disabled = false, minHeight = 118, maxHeight = 200, placeholder, sendLabel, submittingLabel, sendShortcut = "enter", allowAttachmentSend = true, fileBadges = [], isUploading = false, leftSlot, rightSlot, enableVoiceRecording = false, maxVoiceRecordingMs, voiceRecordingDisabledReason, onMessageChange, onSend, onCancel, onFilesSelected, onFileRemove, resolveUrl, onDownload, onPreviewLoad, onVoiceRecordingError, textareaRef, }) => {
    const { t } = useUiTranslation();
    const [isDragging, setIsDragging] = useState(false);
    const handleVoiceRecordingError = (error) => {
        let message = t("chat.voiceRecording.errors.startFailed", "Failed to start voice recording.");
        if (error.code === VoiceRecorderErrorCode.Unsupported) {
            message = t("chat.voiceRecording.errors.unsupported", "Voice recording is not supported in this browser.");
        }
        else if (error.code === VoiceRecorderErrorCode.PermissionDenied) {
            message = t("chat.voiceRecording.errors.permissionDenied", "Microphone permission was denied.");
        }
        else if (error.code === VoiceRecorderErrorCode.EmptyRecording) {
            message = t("chat.voiceRecording.errors.emptyRecording", "Recording did not contain audio.");
        }
        const localizedError = new Error(message);
        localizedError.name = error.name;
        onVoiceRecordingError?.(localizedError);
    };
    const { isSupported: isVoiceRecordingSupported, isStarting: isVoiceRecordingStarting, isRecording, isProcessing: isVoiceRecordingProcessing, elapsedMs: voiceRecordingElapsedMs, waveformSamples, startRecording, stopRecording, } = useVoiceRecorder({
        maxDurationMs: maxVoiceRecordingMs,
        onRecordingComplete: (file) => onFilesSelected?.([file]),
        onError: handleVoiceRecordingError,
    });
    const hasUploadedAttachment = fileBadges.some((file) => file.status === "uploaded" && Boolean(file.uuid));
    const hasAttachmentUploadError = fileBadges.some((file) => file.status === "error");
    const hasPendingAttachment = fileBadges.some((file) => file.status === "pending" || file.status === "uploading");
    const isBusy = isAsking || isSubmitting;
    const isPreparingAttachment = isVoiceRecordingStarting ||
        isVoiceRecordingProcessing ||
        isUploading ||
        hasPendingAttachment;
    const canSend = Boolean(message.trim() || (allowAttachmentSend && hasUploadedAttachment)) &&
        !isBusy &&
        !isPreparingAttachment &&
        !disabled &&
        !isRecording;
    const handleKeyPress = (e) => {
        if (e.shiftKey && e.key === "Enter")
            return;
        if (!canSend)
            return;
        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            if (sendShortcut === "mod-enter" && !(e.metaKey || e.ctrlKey))
                return;
            e.preventDefault();
            onSend?.();
        }
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled &&
            !isBusy &&
            !isUploading &&
            !isRecording &&
            onFilesSelected) {
            setIsDragging(true);
        }
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (disabled || isBusy || isUploading || isRecording || !onFilesSelected)
            return;
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            onFilesSelected(files);
        }
    };
    const voiceRecordingUnavailableReason = !isVoiceRecordingSupported
        ? t("chat.voiceRecording.errors.unsupported", "Voice recording is not supported in this browser.")
        : voiceRecordingDisabledReason;
    const isVoiceRecordingDisabled = disabled ||
        isBusy ||
        isUploading ||
        isVoiceRecordingStarting ||
        isVoiceRecordingProcessing ||
        !onFilesSelected ||
        Boolean(voiceRecordingUnavailableReason);
    let voiceRecordingTitle = voiceRecordingUnavailableReason ||
        t("chat.voiceRecording.start", "Record voice message");
    if (isRecording) {
        voiceRecordingTitle = t("chat.voiceRecording.stop", "Stop recording");
    }
    else if (isVoiceRecordingStarting || isVoiceRecordingProcessing) {
        voiceRecordingTitle = t("chat.voiceRecording.processing", "Processing recording...");
    }
    const voiceRecordingButton = (_jsx(Button, { type: "button", variant: "ghost", size: "icon", className: "h-8 w-8", disabled: isVoiceRecordingDisabled, onClick: isRecording ? stopRecording : startRecording, title: voiceRecordingTitle, children: isRecording ? (_jsx(SquareIcon, { className: "h-4 w-4" })) : (_jsx(MicIcon, { className: "h-4 w-4" })) }));
    return (_jsxs("div", { className: "flex flex-col gap-2 w-full", onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, children: [fileBadges.length > 0 && (_jsx(AttachmentCardList, { className: "mb-1", files: fileBadges, showRemove: true, resolveUrl: resolveUrl, onFileRemove: onFileRemove, onDownload: onDownload, onPreviewLoad: onPreviewLoad })), (isPreparingAttachment || hasAttachmentUploadError) && (_jsx("div", { className: cn("text-xs", isPreparingAttachment && "text-muted-foreground", hasAttachmentUploadError && "text-destructive"), children: hasAttachmentUploadError
                    ? t("chat.attachmentUploadFailed", "Attachment upload failed. Remove it and try again.")
                    : isVoiceRecordingProcessing
                        ? t("chat.voiceRecording.processing", "Processing recording...")
                        : t("chat.uploadingAttachments", "Uploading attachment...") })), isRecording ? (_jsxs("div", { className: cn("flex items-center gap-4 rounded-md border bg-muted/40 px-4 py-3 text-foreground", isDragging && "ring-2 ring-primary ring-offset-2", className), style: {
                    minHeight,
                    maxHeight,
                }, "data-testid": "voice-recording-panel", children: [_jsxs("div", { className: "flex items-center gap-2 text-sm font-medium text-muted-foreground", children: [_jsx("span", { className: "h-2.5 w-2.5 rounded-full bg-destructive animate-pulse" }), _jsx("span", { children: t("chat.voiceRecording.recording", "Recording") })] }), _jsx("div", { className: "flex min-w-0 flex-1 items-center justify-center gap-0.5 overflow-hidden", "aria-hidden": "true", "data-testid": "voice-recording-waveform", children: waveformSamples.map((level, index) => (_jsx("span", { className: "w-1 rounded-full bg-foreground/80 transition-[height] duration-100", style: {
                                height: 10 + level * 38,
                            } }, index))) }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { className: "min-w-10 tabular-nums text-sm font-medium text-muted-foreground", "data-testid": "voice-recording-elapsed", children: formatElapsedTime(voiceRecordingElapsedMs) }), _jsx(Button, { type: "button", variant: "secondary", size: "icon", className: "h-10 w-10 rounded-full", onClick: stopRecording, title: t("chat.voiceRecording.stop", "Stop recording"), children: _jsx(SquareIcon, { className: "h-4 w-4" }) })] })] })) : (_jsx(AutosizeTextarea, { ref: textareaRef, className: cn("text-base focus-visible:ring-0 border-1 resize-none overflow-y-auto overflow-x-hidden w-full", isDragging && "ring-2 ring-primary ring-offset-2", className), placeholder: isDragging
                    ? t("chat.dropFilesPlaceholder", "Drop files here...")
                    : placeholder || t("chat.inputPlaceholder", "Ask me anything..."), minHeight: minHeight, maxHeight: maxHeight, value: message, disabled: isBusy || disabled, onChange: (e) => onMessageChange?.(e.target.value), onKeyDown: handleKeyPress })), _jsxs("div", { className: "flex justify-between items-center mt-2", children: [_jsxs("div", { className: "flex items-center gap-1", children: [onFilesSelected && (_jsx(FileUploadButton, { onFilesSelected: onFilesSelected, disabled: isBusy || isUploading || disabled || isRecording })), enableVoiceRecording &&
                                !isRecording &&
                                !voiceRecordingUnavailableReason &&
                                voiceRecordingButton, leftSlot] }), _jsxs("div", { className: "flex items-center gap-2", children: [rightSlot, isAsking ? (_jsxs(Button, { size: "sm", disabled: disabled, onClick: onCancel, children: [_jsx(SquareIcon, { className: "h-4 w-4" }), _jsx("span", { className: "text-sm hidden sm:inline", children: t("chat.cancel", "Cancel") })] })) : (_jsxs(Button, { size: "sm", disabled: !canSend, onClick: onSend, children: [isPreparingAttachment || isSubmitting ? (_jsx(Loader2Icon, { className: "h-4 w-4 animate-spin" })) : (_jsx(SendIcon, { className: "h-4 w-4" })), _jsx("span", { className: "text-sm hidden sm:inline", children: isSubmitting
                                            ? submittingLabel || t("chat.sending", "Sending...")
                                            : isPreparingAttachment
                                                ? t("chat.uploading", "Uploading...")
                                                : sendLabel || t("chat.send", "Send") })] }))] })] })] }));
};
//# sourceMappingURL=QuestionPanel.js.map